import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Upload,
  ColorPicker,
  Spin,
  Space,
  Typography,
  Divider,
  Tag,
  Tooltip,
  Popconfirm,
  Modal,
} from "antd";
import {
  SaveOutlined,
  UploadOutlined,
  DeleteOutlined,
  GlobalOutlined,
  PictureOutlined,
  SearchOutlined,
  ShareAltOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  StopOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  fetchCareerPageSettings,
  updateCareerPageSettingsThunk,
  uploadCareerPageAsset,
  deleteCareerPageAsset,
  fetchCareerPageLink,
  createCareerPageLinkThunk,
  toggleCareerPageLinkThunk,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  selectCareerPageSettings,
  selectCareerPageSettingsLoading,
  selectCareerPageLink,
  selectCareerPageLinkLoading,
} from "../../../../core/data/redux/recruitment/selectors";

import { DARK_PALETTE } from "../data/brand.colors";
import type { CareerPageSettings as Settings } from "../../../../core/data/redux/recruitment/types";
import { notify } from "../../../../services/toast.config";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const DEFAULT_SETTINGS: Settings = {
  logo_url: null, banner_url: null,
  primary_color: "#0d6efd", company_name: "",
  description: "", meta_title: "", meta_description: "", social_links: {},
};


const Section: React.FC<{
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  description: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}> = ({ icon, iconColor, title, description, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #f0f0f0",
      borderRadius: 10,
      marginBottom: 10,
      overflow: "hidden",
    }}>

      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: open ? "1px solid #f0f0f0" : "none",
          transition: "border-color 0.2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: `${iconColor}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, color: iconColor, flexShrink: 0,
          }}>
            {icon}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
                {title}
              </Title>

              {!open && badge}
            </div>
            {!open && (
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                {description}
              </Text>
            )}
          </div>
        </div>
        <div style={{ color: "#bfbfbf", fontSize: 12, flexShrink: 0 }}>
          {open ? <UpOutlined /> : <DownOutlined />}
        </div>
      </div>


      {open && (
        <div style={{ padding: "20px 20px 20px" }}>
          <Paragraph type="secondary" style={{ margin: "0 0 18px 0", fontSize: 12.5 }}>
            {description}
          </Paragraph>
          {children}
        </div>
      )}
    </div>
  );
};

const CareerPageSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const storedSettings = useSelector(selectCareerPageSettings);
  const loading = useSelector(selectCareerPageSettingsLoading);
  const careerPageLink = useSelector(selectCareerPageLink);
  const careerPageLinkLoading = useSelector(selectCareerPageLinkLoading);
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);

  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [makingLive, setMakingLive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const [colorModalOpen, setColorModalOpen] = useState(false);

  const FEATURED_PALETTE = DARK_PALETTE.slice(0, 10);

  useEffect(() => {
    if (activeCompany?.company_id) {
      dispatch(fetchCareerPageSettings());
      dispatch(fetchCareerPageLink());
    }
  }, [dispatch, activeCompany?.company_id, activeCompany?.company_type]);

  useEffect(() => {
    if (storedSettings) {
      setSettings({
        ...storedSettings,
        logo_url: (storedSettings as any).company_logo_url ?? storedSettings.logo_url ?? null,
        banner_url: (storedSettings as any).banner_image_url ?? storedSettings.banner_url ?? null,
        description: (storedSettings as any).about_company ?? storedSettings.description ?? "",
        meta_title: (storedSettings as any).seo_meta_title ?? storedSettings.meta_title ?? "",
        meta_description: (storedSettings as any).seo_meta_description ?? storedSettings.meta_description ?? "",
      });
    }
  }, [storedSettings]);

  const handleChange = (field: keyof Settings, value: any) =>
    setSettings((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!activeCompany) return;
    setSaving(true);
    try {
      await dispatch(updateCareerPageSettingsThunk(settings)).unwrap();
      notify.success("Career page settings updated");
    } catch (error: any) {
      notify.error(error?.data?.message || error || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file: File, assetType: "logo" | "banner") => {
    if (!activeCompany) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("asset_type", assetType);
      formData.append("file", file);
      formData.append("company_id", activeCompany.company_id);
      formData.append("company_type", activeCompany.company_type);
      const response = await dispatch(uploadCareerPageAsset(formData)).unwrap();
      if (response?.serve_url) {
        setSettings((prev) => ({ ...prev, [`${assetType}_url`]: response.serve_url }));
        notify.success(`${assetType} uploaded successfully`);
      }
    } catch (error: any) {
      notify.error(error?.data?.message || error || `Failed to upload ${assetType}`);
    } finally {
      setSaving(false);
    }
  };

  const handleImageDelete = async (assetType: "logo" | "banner") => {
    if (!activeCompany) return;
    setSaving(true);
    try {
      await dispatch(deleteCareerPageAsset({ assetType })).unwrap();
      handleChange(`${assetType}_url`, null);
      notify.success(`${assetType} removed`);
    } catch (error: any) {
      notify.error(error?.data?.message || error || `Failed to delete ${assetType}`);
    } finally {
      setSaving(false);
    }
  };

  const handleMakeLive = async () => {
    if (!settings.company_name?.trim()) {
      notify.error("Please fill in the Company Name before going live.");
      return;
    }
    setMakingLive(true);
    try {
      await dispatch(createCareerPageLinkThunk()).unwrap();
      notify.success("🎉 Your career page is now live!");
    } catch (error: any) {
      notify.error(error?.data?.message || error?.message || "Failed to go live");
    } finally {
      setMakingLive(false);
    }
  };

  const handleCopyLink = () => {
    if (careerPageLink?.shareable_url) {
      navigator.clipboard.writeText(careerPageLink.shareable_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleToggleStatus = async (newStatus: 0 | 1) => {
    setTogglingStatus(true);
    try {
      await dispatch(toggleCareerPageLinkThunk(newStatus)).unwrap();
      notify.success(
        newStatus === 1
          ? "Career page is back live!"
          : "Career page taken offline. The public URL will no longer be accessible.",
      );
    } catch (error: any) {
      notify.error(error?.data?.message || error?.message || "Failed to update status");
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "80px 0" }}><Spin size="large" /></div>;
  }

  const publicUrl = careerPageLink?.shareable_url || "";


  const publishBadge = careerPageLink ? (
    careerPageLink.status === 1
      ? <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11, lineHeight: "18px" }}>Live</Tag>
      : <Tag icon={<StopOutlined />} color="default" style={{ fontSize: 11, lineHeight: "18px" }}>Offline</Tag>
  ) : (
    <Tag color="warning" style={{ fontSize: 11, lineHeight: "18px" }}>Not Published</Tag>
  );

  return (
    <>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <Title level={5} style={{ margin: 0, fontWeight: 600 }}>Career Page</Title>
          <Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: 13 }}>
            Customise your public job portal — branding, SEO, and social links.
          </Paragraph>
        </div>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          Save Changes
        </Button>
      </div>


      <Section
        icon={<PlayCircleOutlined />}
        iconColor="#52c41a"
        title="Publish Career Page"
        description="Make your career page publicly accessible so candidates can browse and apply for jobs."
        defaultOpen={true}
        badge={publishBadge}
      >
        {careerPageLinkLoading ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
        ) : careerPageLink ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              {careerPageLink.status === 1 ? (
                <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 13, padding: "3px 10px" }}>
                  Live
                </Tag>
              ) : (
                <Tag icon={<StopOutlined />} color="default" style={{ fontSize: 13, padding: "3px 10px" }}>
                  Offline
                </Tag>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {careerPageLink.status === 1
                  ? "Your career page is publicly accessible"
                  : "Your career page is currently offline — candidates cannot access it"}
              </Text>
            </div>

            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: careerPageLink.status === 1 ? "#f6ffed" : "#fafafa",
              border: `1px solid ${careerPageLink.status === 1 ? "#b7eb8f" : "#d9d9d9"}`,
              borderRadius: 8, padding: "10px 14px",
              opacity: careerPageLink.status === 0 ? 0.6 : 1,
            }}>
              <LinkOutlined style={{ color: careerPageLink.status === 1 ? "#52c41a" : "#bfbfbf", flexShrink: 0 }} />
              <Text style={{ flex: 1, fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>
                {publicUrl}
              </Text>
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <Button
                  type="text"
                  size="small"
                  icon={copied ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
                  onClick={handleCopyLink}
                />
              </Tooltip>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              {careerPageLink.status === 1 ? (
                <>
                  <Button size="small" icon={<PlayCircleOutlined />} loading={makingLive} onClick={handleMakeLive}>
                    Regenerate URL
                  </Button>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Creates a new link — old one stops working
                  </Text>
                  <Divider type="vertical" />
                  <Popconfirm
                    title="Take Career Page Offline?"
                    description="Candidates will no longer be able to access your career page."
                    okText="Take Offline"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => handleToggleStatus(0)}
                  >
                    <Button size="small" danger icon={<StopOutlined />} loading={togglingStatus}>
                      Take Offline
                    </Button>
                  </Popconfirm>
                </>
              ) : (
                <>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={togglingStatus}
                    onClick={() => handleToggleStatus(1)}
                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  >
                    Make Live Again
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    This will restore the same public URL.
                  </Text>
                </>
              )}
            </div>
          </div>
        ) : (
          <div>
            {!settings.company_name?.trim() && (
              <div style={{
                background: "#fff7e6", border: "1px solid #ffd591",
                borderRadius: 8, padding: "10px 14px", marginBottom: 12,
              }}>
                <Text style={{ fontSize: 12.5 }}>
                  ⚠️ <strong>Company Name is required</strong> before going live. Fill it in the Visual Identity section below.
                </Text>
              </div>
            )}
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={makingLive}
              disabled={!settings.company_name?.trim()}
              onClick={handleMakeLive}
              style={{
                background: settings.company_name?.trim() ? "#52c41a" : undefined,
                borderColor: settings.company_name?.trim() ? "#52c41a" : undefined,
              }}
            >
              Make Live
            </Button>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 10 }}>
              A unique public URL will be generated for your career page.
            </Text>
          </div>
        )}
      </Section>


      <Section
        icon={<PictureOutlined />}
        iconColor="#1677ff"
        title="Visual Identity"
        description="Upload your logo and banner, set the company name, and choose your brand colour."
        defaultOpen={true}
        badge={
          settings.company_name?.trim()
            ? <Tag color="blue" style={{ fontSize: 11, lineHeight: "18px" }}>{settings.company_name}</Tag>
            : undefined
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <Form.Item label="Company Logo" style={{ margin: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 72, height: 72, borderRadius: 8,
                border: "1px dashed #d9d9d9",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden", background: "#fafafa", flexShrink: 0,
              }}>
                {settings.logo_url
                  ? <img src={settings.logo_url} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  : <PictureOutlined style={{ fontSize: 24, color: "#bfbfbf" }} />}
              </div>
              <div>
                <Space>
                  <Upload showUploadList={false} accept="image/*" beforeUpload={(file) => { handleImageUpload(file, "logo"); return false; }}>
                    <Button size="small" icon={<UploadOutlined />}>Upload</Button>
                  </Upload>
                  {settings.logo_url && (
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleImageDelete("logo")} />
                  )}
                </Space>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>400×400px · PNG/JPG · max 2 MB</Text>
              </div>
            </div>
          </Form.Item>

          <Form.Item label="Brand Colour" style={{ margin: 0 }}>
            <div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {FEATURED_PALETTE.map(({ hex, label }: { hex: any, label: any }) => {
                  const isSelected = settings.primary_color?.toLowerCase() === hex;
                  return (
                    <Tooltip key={hex} title={label}>
                      <div
                        onClick={() => handleChange("primary_color", hex)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: hex,
                          cursor: "pointer",
                          border: isSelected ? "2.5px solid #1677ff" : "2px solid transparent",
                          outline: isSelected ? "2px solid #e6f4ff" : "2px solid #e0e0e0",
                          transition: "all 0.15s",
                          boxSizing: "border-box",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  );
                })}


                <Tooltip title="Browse all 50 colours">
                  <div
                    onClick={() => setColorModalOpen(true)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      border: "1.5px dashed #d9d9d9",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      color: "#8c8c8c",
                      fontWeight: 600,
                      background: "#fafafa",
                      flexShrink: 0,
                      transition: "all 0.15s",
                      userSelect: "none",
                    }}
                  >
                    +40
                  </div>
                </Tooltip>
              </div>


              <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
                Selected:{" "}
                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                  {settings.primary_color}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: settings.primary_color ?? undefined,
                    marginLeft: 6,
                    verticalAlign: "middle",
                    border: "1px solid #d9d9d9",
                  }}
                />
              </Text>
            </div>
          </Form.Item>
        </div>

        <Divider style={{ margin: "20px 0" }} />

        <Form.Item label="Banner Image" style={{ margin: "0 0 16px" }}>
          <div style={{
            width: "100%", height: 100, borderRadius: 8,
            border: "1px dashed #d9d9d9",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", background: "#fafafa", marginBottom: 8,
          }}>
            {settings.banner_url
              ? <img src={settings.banner_url} alt="Banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <PictureOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />}
          </div>
          <Space>
            <Upload showUploadList={false} accept="image/*" beforeUpload={(file) => { handleImageUpload(file, "banner"); return false; }}>
              <Button size="small" icon={<UploadOutlined />}>Upload Banner</Button>
            </Upload>
            {settings.banner_url && (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleImageDelete("banner")} />
            )}
          </Space>
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item label="Company Name" style={{ margin: 0 }}>
            <Input
              placeholder="e.g. Acme Corp"
              value={settings.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
            />
          </Form.Item>
          <div />
        </div>
        <Form.Item label="Company Description" style={{ marginTop: 16, marginBottom: 0 }}>
          <TextArea
            rows={3}
            placeholder="Tell candidates about your company..."
            value={settings.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </Form.Item>
      </Section>





      <Section
        icon={<ShareAltOutlined />}
        iconColor="#1677ff"
        title="Social Presence"
        description="Link your company profiles so candidates can learn more about your culture."
        defaultOpen={true}
        badge={
          (() => {
            const count = Object.values(settings.social_links || {}).filter(Boolean).length;
            return count > 0
              ? <Tag color="blue" style={{ fontSize: 11, lineHeight: "18px" }}>{count} linked</Tag>
              : undefined;
          })()
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[
            { key: "linkedin", label: "LinkedIn", icon: <LinkedinOutlined /> },
            { key: "twitter", label: "Twitter / X", icon: <TwitterOutlined /> },
            { key: "glassdoor", label: "Glassdoor", icon: <GlobalOutlined /> },
          ].map((platform) => (
            <Form.Item key={platform.key} label={platform.label} style={{ margin: 0 }}>
              <Input
                prefix={platform.icon}
                placeholder={`https://${platform.key}.com/company`}
                value={settings.social_links?.[platform.key] || ""}
                onChange={(e) => handleChange("social_links", {
                  ...settings.social_links,
                  [platform.key]: e.target.value,
                })}
              />
            </Form.Item>
          ))}
        </div>
      </Section>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Choose Brand Colour</span>
            <Tag style={{ fontSize: 11, marginLeft: 4 }}>50 dark shades</Tag>
          </div>
        }
        open={colorModalOpen}
        onCancel={() => setColorModalOpen(false)}
        zIndex={1001}
        footer={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Selected:{" "}
              <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{settings.primary_color}</span>
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: settings.primary_color ?? undefined,
                  marginLeft: 6,
                  verticalAlign: "middle",
                  border: "1px solid #d9d9d9",
                }}
              />
            </Text>
            <Button type="primary" onClick={() => setColorModalOpen(false)}>
              Done
            </Button>
          </div>
        }
        styles={{
          content: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 12,
          },
          header: {
            flexShrink: 0,
            padding: "14px 20px 12px",
            borderBottom: "1px solid #f0f0f0",
            marginBottom: 0,
          },
          body: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: "12px 20px 0",
            minHeight: 0,
          },
          footer: {
            flexShrink: 0,
            padding: "10px 20px 14px",
            borderTop: "1px solid #f0f0f0",
            marginTop: 0,
          },
          mask: {
            position: "fixed", inset: 0, width: "100vw", height: "100vh",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0, 0, 0, 0.45)",
          },
        }}
        width={480}
      >

        {[
          { label: "Blues & Navies", range: [0, 10] },
          { label: "Purples & Violets", range: [10, 20] },
          { label: "Greens", range: [20, 30] },
          { label: "Reds & Maroons", range: [30, 40] },
          { label: "Neutrals & Blacks", range: [40, 50] },
        ].map(({ label, range }) => (
          <div key={label} style={{ marginBottom: 20 }}>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>
              {label}
            </Text>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {DARK_PALETTE.slice(range[0], range[1]).map(({ hex, label: colorLabel }) => {
                const isSelected = settings.primary_color?.toLowerCase() === hex;
                return (
                  <Tooltip key={hex} title={colorLabel}>
                    <div
                      onClick={() => handleChange("primary_color", hex)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 7,
                        background: hex,
                        cursor: "pointer",
                        border: isSelected ? "2.5px solid #1677ff" : "2px solid transparent",
                        outline: isSelected ? "2.5px solid #bae0ff" : "2px solid #e0e0e0",
                        transition: "all 0.15s",
                        boxSizing: "border-box",
                        position: "relative",
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          position: "absolute", inset: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <CheckCircleOutlined style={{ color: "#fff", fontSize: 14, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }} />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </Modal>
    </>
  );
};

export default CareerPageSettings;